import { createEvent, createStore, merge, sample } from 'effector'
import { persist } from 'effector-storage/local'
import { fx, Response, ResponseError } from '../../../../libs/request'
import {
  $lastWalletsRequest,
  getWalletsFx,
  walletsRequested,
} from '../../model'
import { WalletGuardWallet, WalletGuardWalletsResponse } from '../../types'
import {
  CreateWalletFeedback,
  CreateWalletRequest,
  CreateWalletValues,
} from './types'

// Как часто запрашивать статус проверки адреса, пока она идёт.
const CHECK_INTERVAL_MS = 90_000

// Короткое название кошелька из адреса: первые и последние 6 символов.
// Подставляется, если пользователь не ввёл название сам.
export const walletNameFromAddress = (address: string) =>
  `${address.slice(0, 6)}...${address.slice(-6)}`

// Собирает запрос на создание кошелька из значений формы: убирает лишние
// пробелы, подставляет название по умолчанию, убирает повторы в платёжных
// системах. Транзитному кошельку добавляет ссылку на финальный.
export const buildCreateWalletRequest = (
  values: CreateWalletValues,
): CreateWalletRequest => {
  const address = values.address.trim()
  const body = {
    address,
    currency: values.currency,
    network: values.network,
    name: values.name?.trim() || walletNameFromAddress(address),
    paymentSystems: Array.from(
      new Set(
        values.paymentSystems?.map((value) => value.trim()).filter(Boolean),
      ),
    ),
    comment: values.comment?.trim() || null,
    // true — только в повторном запросе, когда пользователь подтвердил риск.
    confirmRisk: false,
  }

  if (values.type === 'TRANSIT') {
    if (values.finalWalletId == null) {
      throw new Error('Выберите финальный кошелёк')
    }
    return { ...body, type: 'TRANSIT', finalWalletId: values.finalWalletId }
  }

  return { ...body, type: values.type, finalWalletId: null }
}

// Текст ошибки для пользователя, если кошелёк создать не удалось.
// Сами запрос не повторяем: BitOK мог уже провести проверку,
// а количество проверок ограничено.
const getCreateWalletErrorMessage = (
  error: Response<ResponseError<CreateWalletRequest>>,
) => {
  if (error?.status === 503) {
    return 'AML-проверка временно недоступна. Попробуйте позже.'
  }
  return (
    error?.body?.message ||
    'Не удалось создать кошелёк. Проверьте данные и повторите попытку.'
  )
}

// Запрос на создание кошелька. В ответе — созданный кошелёк.
export const createWalletFx = fx<CreateWalletRequest, WalletGuardWallet>({
  method: 'POST',
  url: '/wallet-guard/wallets',
})

// Запрос созданного кошелька: узнаём, закончилась ли проверка адреса.
export const getCreatedWalletFx = fx<void, WalletGuardWallet>({
  method: 'GET',
})

// Пользователь отправил форму.
export const walletSubmitted = createEvent<CreateWalletValues>()
// Пользователь решил всё равно добавить опасный кошелёк.
export const riskConfirmed = createEvent()
// Пользователь отказался добавлять опасный кошелёк.
export const riskCancelled = createEvent()
// Пользователь закрыл сообщение под формой.
export const feedbackClosed = createEvent()
// Отсчёт до следующего запроса статуса проверки закончился.
export const checkTimerFinished = createEvent()
// Форма открылась: пользователь пришёл на страницу списка.
export const formMounted = createEvent()

// Пришёл кошелёк: в ответе на создание или на запрос статуса проверки.
const walletReceived = merge([
  createWalletFx.doneData,
  getCreatedWalletFx.doneData,
]).map(({ body }) => body)

// Проверка адреса закончилась: статус уже не CHECKING.
const walletCheckFinished = walletReceived.filter({
  fn: (wallet) => wallet.checkStatus !== 'CHECKING',
})

// Флоу 409 WALLET_RISK_CONFIRMATION_REQUIRED отключён: бэк больше не отвечает
// 409 при создании, а создаёт кошелёк сразу и проверяет адрес асинхронно.
// Риск теперь подтверждается после проверки (см. $createdWallet и
// riskConfirmed ниже). Код оставлен на случай, если 409 вернётся. Пока он
// работал, при 409 вместо ошибок валидации и сообщения под формой
// открывалось окно подтверждения.
//
// Если сервер ответил, что адрес опасный и добавление нужно подтвердить
// (ошибка 409), возвращает результат AML-проверки. Иначе — undefined.
// export const getRiskConfirmationAml = (
//   response: Response<unknown>,
// ): WalletRiskAml | undefined => {
//   const body = response?.body as WalletRiskConfirmationError | undefined
//   return response?.status === 409 &&
//     body?.message === 'WALLET_RISK_CONFIRMATION_REQUIRED'
//     ? body.details.aml
//     : undefined
// }
//
// Ошибка создания кошелька вместе с исходным запросом. Если сервер просит
// подтвердить риск, в aml лежит результат проверки. Если риск уже
// подтвердили, а 409 пришёл снова — это обычная ошибка.
// const createWalletFailed = createWalletFx.fail.map(({ params, error }) => ({
//   request: params.body as CreateWalletRequest,
//   error,
//   aml: params.body?.confirmRisk ? undefined : getRiskConfirmationAml(error),
// }))
//
// Запрос и проверка BitOK, которые ждут подтверждения риска. Пока значение
// есть, открыто окно «Высокий риск адреса».
// export const $riskConfirmation = createStore<WalletRiskConfirmation | null>(
//   null,
// )
//   .on(createWalletFailed, (_, { request, aml }) =>
//     aml ? { request, aml } : null,
//   )
//   .reset(createWalletFx.done, riskCancelled)
//
// Подтвердили риск — повторяем тот же запрос целиком, меняются только
// confirmRisk и amlCheckId.
// sample({
//   clock: riskConfirmed,
//   source: $riskConfirmation,
//   filter: Boolean,
//   fn: ({ request, aml }) => ({
//     body: { ...request, confirmRisk: true, amlCheckId: aml.externalId },
//   }),
//   target: createWalletFx,
// })

// Ошибки по конкретным полям формы от сервера. Компонент показывает их
// под нужными полями.
export const createWalletValidationFailed = createWalletFx.failData.filterMap(
  (error) => error?.body?.details?.validationErrors?.items,
)

// Запрос, которым создали кошелёк. Подтверждая риск, повторяем его.
const $createWalletRequest = createStore<CreateWalletRequest | null>(null).on(
  createWalletFx.done,
  (_, { params }) => params.body ?? null,
)

// Созданный кошелёк. Пока адрес проверяется, его статус запрашиваем снова,
// а если бэк просит подтвердить риск — открыто окно «Высокий риск адреса».
// Забываем его при отмене риска и при открытии страницы: что бы ни пришло,
// пока пользователь был на другой странице, вернувшись, он видит только
// список. При ошибке подтверждения риска тоже забываем: окно закрывается,
// и видна ошибка под формой.
export const $createdWallet = createStore<WalletGuardWallet | null>(null)
  .on(walletReceived, (_, wallet) => wallet)
  .reset(riskCancelled, formMounted, createWalletFx.fail)

// Когда снова запросить статус проверки: через 90 секунд после создания
// и после каждого запроса. Под формой идёт обратный отсчёт до этого времени.
export const $nextCheckAt = createStore(0).on(
  [createWalletFx.done, getCreatedWalletFx],
  () => Date.now() + CHECK_INTERVAL_MS,
)

// Сообщение под формой, когда проверка адреса закончилась. При высоком риске
// его нет: вместо него открыто окно подтверждения.
const getCheckFeedback = (
  wallet: WalletGuardWallet,
): CreateWalletFeedback | null => {
  if (wallet.riskConfirmationRequired) {
    return null
  }
  if (wallet.checkStatus === 'ERROR') {
    return {
      type: 'warning',
      message: 'Кошелёк создан, но AML-проверка адреса завершилась с ошибкой.',
    }
  }
  return {
    type: 'success',
    message: 'Кошелёк создан. Проверка адреса завершена.',
  }
}

// Сообщение под формой: чем закончилась проверка адреса или ошибка создания.
// Пока адрес проверяется, сообщения нет — под формой идёт обратный отсчёт.
// Пропадает при новой отправке, когда его закрыли, и при открытии страницы.
export const $createWalletFeedback = createStore<CreateWalletFeedback | null>(
  null,
)
  .on(walletCheckFinished, (_, wallet) => getCheckFeedback(wallet))
  .on(createWalletFx.failData, (_, error) => ({
    type: 'error',
    message: getCreateWalletErrorMessage(error),
  }))
  .reset(createWalletFx, feedbackClosed, formMounted)

// Отправили форму — собираем запрос и создаём кошелёк.
sample({
  clock: walletSubmitted,
  fn: (values) => ({ body: buildCreateWalletRequest(values) }),
  target: createWalletFx,
})

// Подтвердили риск — повторяем тот же запрос с confirmRisk: true.
sample({
  clock: riskConfirmed,
  source: $createWalletRequest,
  filter: Boolean,
  fn: (request) => ({ body: { ...request, confirmRisk: true } }),
  target: createWalletFx,
})

// Отсчёт закончился — запрашиваем созданный кошелёк.
sample({
  clock: checkTimerFinished,
  source: $createdWallet,
  filter: Boolean,
  fn: (wallet) => ({ url: `/wallet-guard/wallets/${wallet.id}` }),
  target: getCreatedWalletFx,
})

// Запрос списка кошельков, здесь — для загрузки финальных.
export const getFinalWalletsFx = fx<void, WalletGuardWalletsResponse>({
  method: 'GET',
  url: '/wallet-guard/wallets',
})

// Нужно загрузить финальные кошельки: выбрали тип «Транзитный»
// или нажали «Повторить» после ошибки.
export const finalWalletsRequested = createEvent()

// Финальные кошельки, к которым можно привязать транзитный.
export const $finalWallets = createStore<WalletGuardWallet[]>([]).on(
  getFinalWalletsFx.doneData,
  (_, { body }) =>
    body.items.filter((wallet) => wallet.type.toUpperCase() === 'FINAL'),
)

// true, если финальные кошельки не удалось загрузить.
// Сбрасывается при новой загрузке.
export const $finalWalletsHasError = createStore(false)
  .on(getFinalWalletsFx.fail, () => true)
  .reset(getFinalWalletsFx)

// Загружаем финальные кошельки (не больше 100).
sample({
  clock: finalWalletsRequested,
  fn: () => ({
    query: {
      limit: '100',
      where: JSON.stringify({ type: ['FINAL'] }),
    },
  }),
  target: getFinalWalletsFx,
})

// Проверка адреса закончилась — обновляем список кошельков на странице
// с теми же параметрами, что и в последний раз. Пока адрес проверяется,
// список не трогаем.
sample({
  clock: walletCheckFinished,
  source: $lastWalletsRequest,
  filter: Boolean,
  target: walletsRequested,
})

// Объединяет два списка платёжных систем: убирает лишние пробелы,
// пустые значения и повторы, сортирует по алфавиту.
const mergePaymentSystems = (current: string[], values: string[]) =>
  Array.from(
    new Set(
      [...current, ...values].map((value) => value.trim()).filter(Boolean),
    ),
  ).sort((a, b) => a.localeCompare(b, 'ru'))

// Варианты для выпадающего списка в поле «Платёжные системы». Выбранные
// значения хранятся в форме: названия из списка
// кошельков и из уже созданных кошельков. Хранятся в localStorage, чтобы
// не пропадали после перезагрузки страницы.
export const $paymentSystems = createStore<string[]>([])
  .on(getWalletsFx.doneData, (current, { body }) =>
    mergePaymentSystems(
      current,
      body.items.flatMap((wallet) => wallet.paymentSystems),
    ),
  )
  .on(createWalletFx.done, (current, { params }) =>
    mergePaymentSystems(current, params.body?.paymentSystems ?? []),
  )

persist({ store: $paymentSystems, key: 'wallet-guard.payment-systems' })
